import { useRef } from 'react';
import { getUserCrispContact } from '../../../lib/api/UsersApi';

// Crisp workspace ("website") that the support team uses for outreach.
const CRISP_WEBSITE_ID = 'cc3f065f-252d-481b-b242-4e2b285ddf09';

// Normalise a phone number to E.164 (+1XXXXXXXXXX). The apps sync the contact's
// phone to Crisp in E.164, and the Contacts `?query=` search does not match a
// bare national number against it — so the +1 is required. Admin holds numbers
// in both shapes, so a 10-digit number is assumed to be US/Canada.
export function toPhoneQuery(phoneNumber) {
  const raw = String(phoneNumber).trim();
  const digits = raw.replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return raw.startsWith('+') ? `+${digits}` : digits;
}

// Crisp deep-links (profile / inbox) are keyed by Crisp's own internal ids, which
// only the admin API can resolve (see openCrispContact). This is the fallback and
// the plain href: the Crisp Contacts page pre-filtered by phone number (which
// matches the contact's synced phone), or by user id when there is no phone.
function toCrispQuery({ phoneNumber, userId }) {
  return (phoneNumber && toPhoneQuery(phoneNumber)) || userId || '';
}

const CRISP_WEBSITE_URL = `https://app.crisp.chat/website/${CRISP_WEBSITE_ID}`;

function generateCrispUrl(query) {
  return `${CRISP_WEBSITE_URL}/contacts/?query=${encodeURIComponent(query)}`;
}

/**
 * Where to send the admin once the admin API has resolved the user's Crisp contact.
 *
 * Exact beats searched: the contact profile when Crisp has one, else the user's newest
 * conversation (the admin API returns them newest first, resolved ones included), else
 * the phone/user-id search. Note the
 * `/profile/` segment — `/contacts/<people_id>/` is not a Crisp route.
 */
export function crispDestination(contact, fallbackUrl) {
  if (contact?.peopleId) {
    return `${CRISP_WEBSITE_URL}/contacts/profile/${encodeURIComponent(contact.peopleId)}/`;
  }
  if (contact?.sessionIds?.length) {
    return `${CRISP_WEBSITE_URL}/inbox/${encodeURIComponent(contact.sessionIds[0])}/`;
  }
  return fallbackUrl;
}

// How long a click waits on the admin API before giving up and sending the tab to the
// search link. The lookup pages through Crisp, so it is not instant, but a blank tab
// that never moves is worse than a search the admin has to finish themselves.
export const CRISP_LOOKUP_TIMEOUT_MS = 4000;

/**
 * Click handler that resolves the user's Crisp contact before navigating.
 *
 * The lookup runs on click, not on render, because this link renders once per rider on
 * the trips view and each lookup pages through Crisp's API. The tab is opened
 * synchronously and pointed at the destination afterwards, since a window.open after an
 * await is treated as an unsolicited popup and blocked. If even that blank tab is
 * blocked, the click is left alone so the anchor's own navigation (the search link)
 * still happens. Any failure or a slow lookup lands on the search link too, and modified
 * clicks (new tab, copy link) skip the lookup entirely.
 */
export const openCrispContact = ({ userId, fallbackUrl }) => async (event) => {
  if (!userId || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
    return;
  }

  const tab = window.open('', '_blank');
  if (!tab) {
    return;
  }
  event.preventDefault();
  tab.opener = null;

  let timer;
  const timeout = new Promise((resolve) => {
    timer = setTimeout(() => resolve(null), CRISP_LOOKUP_TIMEOUT_MS);
  });
  let destination = fallbackUrl;
  try {
    const contact = await Promise.race([getUserCrispContact({ id: userId }), timeout]);
    destination = crispDestination(contact, fallbackUrl);
  } catch (err) {
    // Fall through to the search link.
  } finally {
    clearTimeout(timer);
  }

  tab.location.href = destination;
};

/**
 * The same link this component renders, for callers that want their own presentation.
 *
 * Exported rather than reimplemented because the query is not obvious: it is a phone
 * number normalised to national significant digits, NOT the admin user id, and getting
 * that wrong produces a link that opens the unfiltered contacts list. Null when there
 * is no usable identifier, for the same reason the component renders nothing.
 */
export const crispContactUrl = ({ userId, phoneNumber }) => {
  const query = toCrispQuery({ phoneNumber, userId });
  return query ? generateCrispUrl(query) : null;
};

const CrispLogo = ({ size = 20 }) => {
  // Unique gradient id per instance — this icon renders many times on the
  // trips view, and duplicate ids in the DOM would clash.
  const gradientIdRef = useRef(null);
  if (gradientIdRef.current === null) {
    gradientIdRef.current = `crispGradient-${Math.random().toString(36).slice(2)}`;
  }
  const gradientId = gradientIdRef.current;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0.45" y2="1">
          <stop offset="0" stopColor="#34A9F5" />
          <stop offset="1" stopColor="#2A78E8" />
        </linearGradient>
      </defs>
      {/* Gradient tile background */}
      <rect x="0" y="0" width="24" height="24" rx="5.4" fill={`url(#${gradientId})`} />
      {/* White speech bubble with downward tail, slightly tilted */}
      <g transform="rotate(-7 12 11.5)" fill="#ffffff">
        <rect x="5" y="6.6" width="14" height="8" rx="1" />
        <path d="M11 13.8H14.2L11.4 18z" />
      </g>
    </svg>
  );
};

const UserCrispLink = ({ userId, phoneNumber }) => {
  const query = toCrispQuery({ phoneNumber, userId });
  // Without an identifier the link would open the full, unfiltered Crisp
  // contacts list, which is misleading — don't render it.
  if (!query) {
    return null;
  }

  const href = generateCrispUrl(query);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
      <a
        href={href}
        onClick={openCrispContact({ userId, fallbackUrl: href })}
        target="_blank"
        rel="noopener noreferrer"
        title="Open this user in Crisp"
        style={{ display: 'flex', alignItems: 'center' }}
      >
        <CrispLogo />
      </a>
    </span>
  );
};

export default UserCrispLink;
